// src/components/molecules/calls/call-info-modals/call-info-modals.tsx

import { useState } from 'react';
import { SettingName } from 'src/api/generated';
import RecordingPlayer from '../recording-player/recording-player';
import SummaryModalContent from 'src/components/molecules/analysis/summary-modal-content/summary-modal-content';
import FeedbackModalContent from 'src/components/molecules/analysis/feedback-modal-content/feedback-modal-content';
import ButtonModal from 'src/modules/shared/components/molecules/modals/button-modal';
import {
  PiStar,
  PiChatCenteredText,
  PiUser,
  PiGraduationCap,
} from 'react-icons/pi';
import TranscriptionModalContent from 'src/components/molecules/analysis/transcription-modal-content/transcription-modal-content';
import { useFeatureFlagActive } from 'hooks/settings/feature-flags';
import { Tooltip } from '@mui/material';
import { MIN_PERFORMANCE_SCORE } from 'src/utils/analysisValues/scores/scores';

interface CallInfoModalsProps {
  callId: number;
  callAgentScore: number;
  buttonClassName?: string;
  iconClassName?: string;
  playerHeight?: number;
  playerWidth?: number;
  visible?: boolean;
}

const CallInfoModals: React.FC<CallInfoModalsProps> = ({
  callId,
  callAgentScore,
  buttonClassName = '',
  iconClassName = '',
  playerHeight = 40,
  playerWidth = 300,
  visible = true,
}) => {
  const [copyableSummary, setCopyableSummary] = useState<string>('');
  const [copyableTranscription, setCopyableTranscription] =
    useState<string>('');

  const showRecordings = useFeatureFlagActive(
    SettingName.FEATURE_FLAG_SHOW_RECORDINGS
  );
  const showTranscriptions = useFeatureFlagActive(
    SettingName.FEATURE_FLAG_SHOW_TRANSCRIPTIONS
  );
  // TODO: chen when implementing i18n
  const transcriptionModalTitle = `Transcripción de la llamada`;
  const summaryModalTitle = `Resumen de la llamada`;
  const feedbackModalTitle = 'Feedback de la llamada';

  if (!visible) return null;

  const isFeatured = callAgentScore >= MIN_PERFORMANCE_SCORE;

  const baseIconClasses = iconClassName || 'h-5 w-5 text-primary-700';

  return (
    <div className="flex justify-center items-center gap-2">
      {isFeatured ? (
        <Tooltip
          title={'Llamada destacada'}
          placement="top"
          PopperProps={{
            // workaround to avoid tooltip being hidden behind modal
            style: { zIndex: 10000 },
          }}
        >
          <PiStar className={`h-5 w-5 text-info-600 ${iconClassName}`} />
        </Tooltip>
      ) : (
        <div className="h-5 w-5" />
      )}

      {showRecordings && (
        <RecordingPlayer
          callId={callId}
          iconClassName={baseIconClasses}
          buttonClassName={buttonClassName}
          playerHeight={playerHeight}
          playerWidth={playerWidth}
        />
      )}

      {showTranscriptions && (
        <ButtonModal
          variant="neutral"
          appearance="soft"
          size="sm"
          buttonContent={<PiChatCenteredText className={baseIconClasses} />}
          title={transcriptionModalTitle}
          className={buttonClassName}
          copyableContent={copyableTranscription}
        >
          {(enabled: boolean) => (
            <TranscriptionModalContent
              callId={callId}
              enabled={enabled}
              onCopyableContentChange={setCopyableTranscription}
            />
          )}
        </ButtonModal>
      )}

      <ButtonModal
        variant="neutral"
        appearance="soft"
        size="sm"
        buttonContent={<PiUser className={baseIconClasses} />}
        title={summaryModalTitle}
        className={buttonClassName}
        copyableContent={copyableSummary}
        bodyClassName={'!p-0'}
      >
        {(enabled: boolean) => (
          <SummaryModalContent
            callId={callId}
            enabled={enabled}
            onCopyableContentChange={setCopyableSummary}
          />
        )}
      </ButtonModal>

      <ButtonModal
        variant="neutral"
        appearance="soft"
        size="sm"
        buttonContent={<PiGraduationCap className={baseIconClasses} />}
        title={feedbackModalTitle}
        className={buttonClassName}
        bodyClassName={'!p-0'}
      >
        {(enabled: boolean) => (
          <FeedbackModalContent callId={callId} enabled={enabled} />
        )}
      </ButtonModal>
    </div>
  );
};

export default CallInfoModals;
